/**
 * Is a merchant's accounting connection pointed at a LIVE accounting system?
 *
 * Drives the ConfigLock on the Fiken / Tripletex / Unimicro pages. A connection to a real ledger loads
 * locked, so nobody edits credentials, a department or an auto-sync toggle on production by reflex —
 * the same guard the System → Config pages use. A test connection stays editable, because that is what
 * a test environment is for.
 *
 * **Fail safe: anything not RECOGNISED as a test URL counts as production.** The obvious alternative —
 * listing the known production hosts — fails OPEN the day a provider adds or renames one, and it fails
 * silently: an unlocked form looks exactly like a form that was never meant to be locked. This way an
 * unfamiliar URL costs one click, and the mistake is never the dangerous one.
 */
const KNOWN_TEST_URLS = [
  'https://test.unimicro.no',
  'https://test-login.unimicro.no',
  'https://api-test.tripletex.tech/v2'
];

function isKnownTestUrl(url) {
  return KNOWN_TEST_URLS.includes(String(url).trim().toLowerCase().replace(/\/+$/, ''));
}

export default {
  /**
   * True if ANY of the given endpoint URLs is not a known test URL.
   *
   * `some`, deliberately, not `every`. An account can sit half-switched — Unimicro keeps the API URL and
   * the login server as two separate fields, so one can point at production while the other still points
   * at test — and in that state real credentials and real bookkeeping are already at stake. Requiring
   * every field to look live would leave exactly that case unlocked.
   *
   * No URLs at all (a location that has never been set up) is not production: there is nothing to guard.
   */
  isProduction(...urls) {
    const configured = urls.map((u) => (u == null ? '' : String(u).trim())).filter(Boolean);
    if (!configured.length) return false;
    return configured.some((u) => !isKnownTestUrl(u));
  },

  /**
   * Fiken, which has no test environment in this integration — there is only the live API.
   *
   * So the question is not which environment but whether it is connected at all. Kept here rather than
   * inlined in the page so the asymmetry between the three integrations is visible in one file instead
   * of being something you notice by reading all three.
   */
  fikenIsProduction(account) {
    return !!(account && String(account.clientId || '').trim());
  }
};
